import { useState, useEffect, useCallback, useRef } from 'react'
import { recordActivity } from '../services/sessionActivity'

/**
 * Hook to automatically log out users after a period of inactivity.
 *
 * Features:
 * - Tracks user activity (mouse, keyboard, click, scroll, touch)
 * - Shows a warning before auto-logout
 * - Allows user to extend their session
 * - Only active when user is authenticated
 *
 * @param {Object} options
 * @param {boolean} options.isAuthenticated - Whether user is currently logged in
 * @param {function} options.onLogout - Callback to log out the user
 * @param {number} options.inactivityTimeout - Time in ms before showing warning (default: 29 minutes)
 * @param {number} options.warningDuration - Time in ms to show warning before logout (default: 1 minute)
 */
export function useInactivityLogout({
  isAuthenticated,
  onLogout,
  inactivityTimeout = 29 * 60 * 1000, // 29 minutes
  warningDuration = 60 * 1000, // 1 minute
}) {
  const [showWarning, setShowWarning] = useState(false)
  const [secondsRemaining, setSecondsRemaining] = useState(0)

  const inactivityTimerRef = useRef(null)
  const warningTimerRef = useRef(null)
  const countdownIntervalRef = useRef(null)
  // Track warning state in a ref so activity handlers can access current value
  // without causing effect re-runs
  const showWarningRef = useRef(false)

  // Clear all timers
  const clearAllTimers = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current)
      inactivityTimerRef.current = null
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current)
      warningTimerRef.current = null
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current)
      countdownIntervalRef.current = null
    }
  }, [])

  // Start the logout countdown (called when warning is shown)
  const startLogoutCountdown = useCallback(() => {
    const seconds = Math.floor(warningDuration / 1000)
    setSecondsRemaining(seconds)
    setShowWarning(true)
    showWarningRef.current = true

    // Update countdown every second
    countdownIntervalRef.current = setInterval(() => {
      setSecondsRemaining(prev => {
        if (prev <= 1) {
          return 0
        }
        return prev - 1
      })
    }, 1000)

    // Auto-logout after warning duration
    warningTimerRef.current = setTimeout(() => {
      clearAllTimers()
      setShowWarning(false)
      showWarningRef.current = false
      onLogout()
    }, warningDuration)
  }, [warningDuration, onLogout, clearAllTimers])

  // Reset the inactivity timer (called on user activity)
  const resetInactivityTimer = useCallback(() => {
    // Don't reset if not authenticated
    if (!isAuthenticated) return

    // Persist last-activity time so the idle policy survives a tab close (#517).
    // Covers initial mount, each throttled activity event, and "Stay logged in".
    recordActivity()

    // Clear existing timers
    clearAllTimers()
    setShowWarning(false)
    showWarningRef.current = false
    setSecondsRemaining(0)

    // Start new inactivity timer
    inactivityTimerRef.current = setTimeout(() => {
      startLogoutCountdown()
    }, inactivityTimeout)
  }, [isAuthenticated, inactivityTimeout, clearAllTimers, startLogoutCountdown])

  // Extend session (called when user clicks "Stay Logged In")
  const extendSession = useCallback(() => {
    resetInactivityTimer()
  }, [resetInactivityTimer])

  // Set up event listeners for user activity
  useEffect(() => {
    if (!isAuthenticated) {
      clearAllTimers()
      setShowWarning(false)
      showWarningRef.current = false
      return
    }

    // Activity events to track
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'keydown',
      'scroll',
      'touchstart',
      'click',
      'wheel',
    ]

    // Throttle activity detection to avoid excessive timer resets
    let lastActivity = Date.now()
    const throttleMs = 1000 // Only reset timer once per second max

    const handleActivity = () => {
      const now = Date.now()
      if (now - lastActivity >= throttleMs) {
        lastActivity = now
        // Only reset if warning is not showing (use ref for current value)
        // (if warning is showing, user must click "Stay Logged In")
        if (!showWarningRef.current) {
          resetInactivityTimer()
        }
      }
    }

    // Add event listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true })
    })

    // Start initial timer
    resetInactivityTimer()

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity)
      })
      clearAllTimers()
    }
  // Note: showWarning removed from deps - we use showWarningRef instead
  // to avoid resetting the timer when the warning appears
  }, [isAuthenticated, resetInactivityTimer, clearAllTimers])

  return {
    showWarning,
    secondsRemaining,
    extendSession,
  }
}

export default useInactivityLogout
